<template>
    <div class="tw-min-h-screen">
        <ts-page-title
            :title="$t('assignDriver.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('assignDriver.pageTitle'),
                    href: '/admin/agency/driver-assigns'
                },
                {
                    text: $t('create'),
                    active: true
                }
            ]"
        />

        <ts-loading-banner :loading="loading">
            <div class="assign-page-content">
            <DriverSetupSection
                :driver-id="model.driver_id"
                :driver-list="driverList"
                :errors="errors"
                @change-driver="model.driver_id = $event"
                @search-driver="handleDriverSearch"
            />

            <div class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-4">
                <AvailablePackagesPanel
                    :rows="availableRows"
                    :selected-ids="model.booking_list_id"
                    :search-box="search_box"
                    :receiver-phone="receiver_phone_search"
                    :zone-id="zone_id"
                    :zone-list="zoneList"
                    @update-search-box="search_box = $event"
                    @update-receiver-phone="receiver_phone_search = $event"
                    @search-packages="searchPackages"
                    @search-receiver-phone="searchReceiverPhone"
                    @search-zone="handleZoneSearch"
                    @change-zone="onZoneChange"
                    @clear-filters="clearFilters"
                    @select-all="selectAllMatching"
                    @add="addItem"
                />

                <SelectedPackagesPanel
                    :records="selectedBooking"
                    :total-packages="totalSelectedPackages"
                    :errors="errors"
                    @clear="clearSelection"
                    @delete="deleteRecord"
                />
            </div>
            </div>
        </ts-loading-banner>

        <AssignActionFooter
            :selected-driver-name="selectedDriverName"
            :selected-count="selectedBooking.length"
            :can-assign="canAssign"
            @clear="clearSelection"
            @save="onSave"
        />

        <a-modal
            :title="$t('pdf')"
            width="50%"
            v-model="preview_pdf"
            :footer="null"
        >
            <ts-preview-pdf v-model="waiting" :src="src" />
        </a-modal>
    </div>
</template>

<script>
import { mapActions } from "vuex";
import { debounce } from "lodash";
import { Errors } from "form-backend-validation";
import AssignActionFooter from "./components/AssignActionFooter.vue";
import AvailablePackagesPanel from "./components/AvailablePackagesPanel.vue";
import DriverSetupSection from "./components/DriverSetupSection.vue";
import SelectedPackagesPanel from "./components/SelectedPackagesPanel.vue";
export default {
    name: "assignDriverCreate",
    components: {
        AssignActionFooter,
        AvailablePackagesPanel,
        DriverSetupSection,
        SelectedPackagesPanel
    },
    data() {
        return {
            waiting: false,
            preview_pdf: false,
            src: "",
            errors: new Errors(),
            loading: false,
            search_box: null,
            zone_id: null,
            bookingList: [],
            selectedBooking: [],
            model: {
                driver_id: null,
                booking_list_id: []
            },
            receiver_phone_search: null,
            last_match_count: 0,
            driverList: [],
            zoneList: []
        };
    },
    computed: {
        selectedDriver() {
            return this.driverList.find(
                driver => driver.driver_id === this.model.driver_id
            );
        },
        selectedDriverName() {
            return this.selectedDriver
                ? this.selectedDriver.driver_name
                : this.$t("assignDriver.driver");
        },
        canAssign() {
            return !!this.model.driver_id && this.selectedBooking.length > 0;
        },
        availableRows() {
            return this.bookingList.filter(record => !this.isSelected(record));
        },
        totalSelectedPackages() {
            return this.selectedBooking.reduce((total, record) => {
                return total + Number(record.number_of_package || 0);
            }, 0);
        }
    },
    methods: {
        ...mapActions("agency/assignDriver", [
            "store",
            "searchBooking",
            "getFormViewData"
        ]),
        searchPackages() {
            this.onSearchInput();
        },
        loadAvailablePackages() {
            this.getBooking({ limit: 300 });
        },
        searchReceiverPhone() {
            this.onSearchInput();
        },
        clearFilters() {
            this.search_box = null;
            this.receiver_phone_search = null;
            this.zone_id = null;
            this.bookingList = [];
            this.last_match_count = 0;
        },
        clearSelection() {
            this.selectedBooking = [];
            this.model.booking_list_id = [];
        },
        isSelected(record) {
            return this.model.booking_list_id.some(id => id == record.booking_id);
        },
        selectAllMatching() {
            this.availableRows.forEach(record => this.addItem(record));
        },
        handleDriverSearch: debounce(async function(value) {
            this.loading = true;

            try {
                let rep = await this.getFormViewData({
                    index: "driver",
                    params: {
                        fnName: "driver",
                        search_driver: value
                    }
                });
                this.driverList = rep.data["driver"] || [];
            } catch (error) {
                this.$notify({ type: "error", text: error.message });
            } finally {
                this.loading = false;
            }
        }, 1000),
        handleZoneSearch: debounce(async function(value) {
            if (!value) return false;
            this.loading = true;

            try {
                var rep = await this.getFormViewData({
                    index: "zone",
                    params: {
                        fnName: "zone",
                        search_zone: value
                    }
                });
                this.zoneList = rep.data["zone"] || [];
            } catch (error) {
                this.$notify({ type: "error", text: error.message });
            } finally {
                this.loading = false;
            }
        }, 1000),
        onZoneChange(value) {
            this.zone_id = value || null;
        },
        onSearchInput() {
            this.getBooking({
                zone_id: this.zone_id,
                search_booking: this.search_box,
                receiver_phone: this.receiver_phone_search,
                limit: 300
            });
        },
        getBooking(value) {
            this.loading = true;
            this.searchBooking(value)
                .then(response => {
                    this.bookingList = response.data || [];
                    this.last_match_count = this.bookingList.length;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        async fetchFormOptions() {
            this.loading = true;

            try {
                let rep = await this.getFormViewData({
                    index: "driver,zone",
                    params: {
                        fnName: "driver,zone"
                    }
                });
                this.driverList = rep.data["driver"] || [];
                this.zoneList = rep.data["zone"] || [];
            } catch (error) {
                this.$notify({ type: "error", text: error.message });
            } finally {
                this.loading = false;
            }
        },
        addItem(item) {
            var existing = this.model.booking_list_id.find(
                id => id == item.booking_id
            );
            if (!existing) {
                this.selectedBooking.push(item);
                this.model.booking_list_id.push(item.booking_id);
            }
        },
        deleteRecord(record) {
            this.selectedBooking.splice(
                this.selectedBooking.indexOf(
                    this.selectedBooking.find(
                        b => b.booking_id == record.booking_id
                    )
                ),
                1
            );

            this.model.booking_list_id.splice(
                this.model.booking_list_id.indexOf(record.booking_id),
                1
            );
        },
        onSave() {
            this.src = "";
            this.errors = new Errors();
            this.waiting = true;
            this.preview_pdf = true;
            this.store(this.model)
                .then(response => {
                    this.$notify({ type: "success", text: response.message });
                    this.clearInput();
                    this.src = response.url;
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.$notify({ type: "error", text: error.message });
                    this.preview_pdf = false;
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        clearInput() {
            this.bookingList = [];
            this.selectedBooking = [];
            (this.model.driver_id = undefined),
                (this.model.booking_list_id = []);
            this.removeKeyData();
        },
        setValueModel() {
            if (localStorage.getItem("model_assign")) {
                var data = JSON.parse(localStorage.getItem("model_assign"));
                this.bookingList = data.bookingList;
                this.selectedBooking = data.selectedBooking;
                this.model = data.model;
                this.receiver_phone_search = data.receiver_phone_search || null;
                this.last_match_count = data.last_match_count || 0;
                this.driverList = data.driverList;
                this.zoneList = data.zoneList || [];
            }
        },
        removeKeyData() {
            localStorage.removeItem("model_assign");
        }
    },
    watch: {
        model: {
            handler() {
                localStorage.setItem(
                    "model_assign",
                    JSON.stringify({
                        model: this.model,
                        selectedBooking: this.selectedBooking,
                        bookingList: this.bookingList,
                        receiver_phone_search: this.receiver_phone_search,
                        last_match_count: this.last_match_count,
                        driverList: this.driverList,
                        zoneList: this.zoneList
                    })
                );
            },
            deep: true
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.setValueModel();
            vm.fetchFormOptions();
            vm.loadAvailablePackages();
        });
    }
};
</script>

<style>
.assign-page-content {
    padding-bottom: 92px;
}

.available-package-scroll {
    height: 404px;
    overflow-y: auto;
}


.selected-package-scroll {
    height: 560px;
    overflow-y: auto;
}

.selected-package-empty {
    height: 100%;
}

.assign-page-footer {
    bottom: 0;
    min-height: 56px;
    left: 220px;
    position: fixed;
    right: 0;
    z-index: 1020;
}

.app-sidebar-minified .assign-page-footer {
    left: 60px;
}

.app-without-sidebar .assign-page-footer,
.app-sidebar-mobile-toggled .assign-page-footer {
    left: 0;
}

@media (max-width: 991.98px) {
    .assign-page-content {
        padding-bottom: 132px;
    }

    .assign-page-footer {
        align-items: stretch;
        flex-direction: column;
        left: 0;
    }
}
</style>
